import Phaser from 'phaser';
import { GAME_W, GAME_H, setupCamera } from '../dpr.js';
import { GameStateManager } from '../state/GameStateManager';
import { formatAffix, type Sigil } from '@roe/shared';

/**
 * Pre-battle sigil loadout + insurance modal.
 *
 * Launched from HeroSelectScene after the player picks their starting hero, and
 * from the post-death "TRY AGAIN" button. Lets the player pick up to
 * `effectiveSigilSlots()` sigils from `account.sigilCollection`, optionally
 * tick each one as "insured" (10 runes a piece), then commits the transaction
 * via `GameStateManager.startRunWithSigils()` — which moves the picks into
 * `player.sigilBag` with an `insured` flag and charges the insurance total.
 *
 * Uninsured bag sigils drop on death; insured ones auto-return to the collection.
 * Skipping is always allowed (confirm with zero picks).
 *
 * Visual style mirrors the InventoryScene sigil collection — bordered row cards,
 * icon on the left, name + affixes stacked to the right. Rendered as a centered
 * panel (not fullscreen) over a dim overlay so the scene feels like a modal.
 */
const INSURANCE_PRICE = 10;

// Panel geometry — centered modal over the game area.
const PANEL_W = 680;
const PANEL_H = 500;
const PANEL_CX = GAME_W / 2;
const PANEL_CY = GAME_H / 2;
const PANEL_LEFT = PANEL_CX - PANEL_W / 2;
const PANEL_TOP  = PANEL_CY - PANEL_H / 2;
const PANEL_RIGHT = PANEL_CX + PANEL_W / 2;
const PANEL_BOTTOM = PANEL_CY + PANEL_H / 2;

export class SigilLoadoutScene extends Phaser.Scene {
  private gsm!: GameStateManager;
  private collection: Sigil[] = [];
  private picks: Array<{ sigilId: string; insured: boolean } | null> = [];
  private slotCount = 1;

  // Redrawable roots — scene.create() builds a static frame; rebuildBody() redraws
  // the dynamic middle (slot cards + collection list + total) on each state change.
  private bodyRoot!: Phaser.GameObjects.Container;
  private runeTxt!: Phaser.GameObjects.Text;
  private totalTxt!: Phaser.GameObjects.Text;
  private confirmBtn!: Phaser.GameObjects.Text;
  private confirmBg!: Phaser.GameObjects.Rectangle;

  constructor() {
    super({ key: 'SigilLoadoutScene' });
  }

  init(data: { gsm?: GameStateManager }): void {
    this.gsm = data.gsm || GameStateManager.getInstance();
  }

  create(): void {
    setupCamera(this, this.cameras.main, 'full');
    // Dark backdrop (visible behind the modal panel — the BattleScene isn't alive at this
    // point so we paint our own dim tone instead of relying on a running scene behind us).
    this.cameras.main.setBackgroundColor('#06040a');
    if (!this.gsm) this.gsm = GameStateManager.getInstance();

    this.collection = [...(this.gsm.account.sigilCollection ?? [])];
    this.slotCount = this.gsm.effectiveSigilSlots();
    this.picks = Array.from({ length: this.slotCount }, () => null);

    // No sigils at all → nothing to pick, just start the run with an empty bag.
    if (this.collection.length === 0) {
      this.gsm.startRunWithSigils([]);
      this.scene.start('BookBanScene');
      return;
    }

    // Dim overlay behind the panel — mirrors the Delete Save / Tome Choice popup style.
    this.add.rectangle(GAME_W / 2, GAME_H / 2, GAME_W, GAME_H, 0x000000, 0.65).setDepth(1);

    // ── Modal panel frame ──
    this.add.rectangle(PANEL_CX, PANEL_CY, PANEL_W, PANEL_H, 0x0d0a08, 0.98)
      .setStrokeStyle(2, 0xc8973a, 0.85).setDepth(5);
    // Inner hint border
    this.add.rectangle(PANEL_CX, PANEL_CY, PANEL_W - 8, PANEL_H - 8, 0, 0)
      .setStrokeStyle(1, 0xc8973a, 0.25).setDepth(5);

    // ── Header bar (title row) ──
    const headerY = PANEL_TOP + 28;
    this.add.text(PANEL_CX, headerY, '✦  Run Loadout  ✦', {
      fontSize: '16px', fontFamily: 'Georgia, serif', color: '#c8973a', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(10);
    // Header divider
    this.add.rectangle(PANEL_CX, headerY + 16, PANEL_W - 40, 1, 0x5a3600, 0.9).setDepth(10);

    // Close ✕ button — calls confirmAndStart(skipAll=true) so the player never ends up
    // stuck in the modal. Mirrors the close-X pattern used everywhere else.
    const closeBtn = this.add.text(PANEL_RIGHT - 20, headerY, '✕', {
      fontSize: '14px', fontFamily: 'Georgia, serif', color: '#5a3600',
    }).setOrigin(0.5).setInteractive({ useHandCursor: true }).setDepth(11);
    closeBtn.on('pointerover', () => closeBtn.setColor('#c8973a'));
    closeBtn.on('pointerout', () => closeBtn.setColor('#5a3600'));
    const doSkip = () => this.confirmAndStart(true);
    closeBtn.on('pointerdown', doSkip);
    this.input.keyboard?.once('keydown-ESC', doSkip);

    // ── Hint line under the header ──
    this.add.text(PANEL_CX, headerY + 32,
      `Bring up to ${this.slotCount} sigil${this.slotCount === 1 ? '' : 's'} — insure each (${INSURANCE_PRICE} ◆) to guarantee its return on death.`, {
      fontSize: '10px', fontFamily: 'Georgia, serif', color: '#a07840',
      wordWrap: { width: PANEL_W - 60 }, align: 'center',
    }).setOrigin(0.5).setDepth(10);

    // ── Rune balance (top-left) + insurance total (top-right), both above the slots ──
    this.runeTxt = this.add.text(PANEL_LEFT + 20, headerY + 56, '', {
      fontSize: '11px', fontFamily: 'Georgia, serif', color: '#c8973a', fontStyle: 'bold',
    }).setOrigin(0, 0.5).setDepth(10);
    this.totalTxt = this.add.text(PANEL_RIGHT - 20, headerY + 56, '', {
      fontSize: '11px', fontFamily: 'Georgia, serif', color: '#c8973a',
    }).setOrigin(1, 0.5).setDepth(10);

    // ── Footer: Skip (left) + Confirm (right) ──
    const footerY = PANEL_BOTTOM - 28;
    const skipBg = this.add.rectangle(PANEL_CX - 110, footerY, 140, 30, 0x1a0f08, 1)
      .setStrokeStyle(1, 0x5a3600).setInteractive({ useHandCursor: true }).setDepth(10);
    const skipTxt = this.add.text(PANEL_CX - 110, footerY, 'SKIP', {
      fontSize: '11px', fontFamily: 'Georgia, serif', color: '#8a6420', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(11);
    skipBg.on('pointerover', () => { skipTxt.setColor('#c8973a'); skipBg.setStrokeStyle(1, 0x8a6420); });
    skipBg.on('pointerout',  () => { skipTxt.setColor('#8a6420'); skipBg.setStrokeStyle(1, 0x5a3600); });
    skipBg.on('pointerdown', doSkip);
    skipTxt.on('pointerdown', doSkip);

    this.confirmBg = this.add.rectangle(PANEL_CX + 110, footerY, 180, 30, 0x1a0608, 1)
      .setStrokeStyle(2, 0x8b2000).setInteractive({ useHandCursor: true }).setDepth(10);
    this.confirmBtn = this.add.text(PANEL_CX + 110, footerY, 'CONFIRM & BEGIN', {
      fontSize: '11px', fontFamily: 'Georgia, serif', color: '#e8a030', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(11);
    const doConfirm = () => this.confirmAndStart(false);
    this.confirmBg.on('pointerover', () => { this.confirmBtn.setColor('#ffe8a0'); this.confirmBg.setStrokeStyle(2, 0xc84000); });
    this.confirmBg.on('pointerout',  () => { this.confirmBtn.setColor('#e8a030'); this.confirmBg.setStrokeStyle(2, 0x8b2000); });
    this.confirmBg.on('pointerdown', doConfirm);
    this.confirmBtn.on('pointerdown', doConfirm);

    this.bodyRoot = this.add.container(0, 0).setDepth(8);
    this.rebuildBody();
  }

  private rebuildBody(): void {
    this.bodyRoot.removeAll(true);
    const runes = this.gsm.player.resources.loot ?? 0;
    const insuredCount = this.picks.filter((p) => p?.insured).length;
    const total = insuredCount * INSURANCE_PRICE;
    const canAfford = runes >= total;

    this.runeTxt.setText(`◆ ${runes}`);
    this.totalTxt.setText(`Insurance: ${total} ◆`).setColor(canAfford ? '#c8973a' : '#a83030');
    this.confirmBtn.setAlpha(canAfford ? 1 : 0.45);
    this.confirmBg.setAlpha(canAfford ? 1 : 0.45);

    // ── Slot strip — compact rows mirroring the collection card style (icon + name + affix) ──
    const slotY = PANEL_TOP + 118;
    const slotW = Math.min(200, (PANEL_W - 60) / Math.max(1, this.slotCount) - 12);
    // Height bumped from 60 → 86 so a two-line special.description fits below the
    // affix line without overlapping the Insure checkbox along the bottom edge.
    const slotH = 86;
    const slotGap = 12;
    const totalSlotW = this.slotCount * slotW + (this.slotCount - 1) * slotGap;
    const slotStartX = PANEL_CX - totalSlotW / 2 + slotW / 2;

    for (let i = 0; i < this.slotCount; i++) {
      const cx = slotStartX + i * (slotW + slotGap);
      const pick = this.picks[i];
      const sigil = pick ? this.collection.find((s) => s.id === pick.sigilId) : null;

      const bg = this.add.rectangle(cx, slotY, slotW, slotH, sigil ? 0x1a120a : 0x080604, 0.95)
        .setStrokeStyle(1, sigil ? 0xc0912a : 0x3a2a18);
      this.bodyRoot.add(bg);

      // Slot label — tiny header above the row
      this.bodyRoot.add(this.add.text(cx - slotW / 2 + 6, slotY - slotH / 2 + 4, `SLOT ${i + 1}`, {
        fontSize: '8px', fontFamily: 'Georgia, serif', color: '#8a6420',
      }).setOrigin(0, 0));

      if (sigil) {
        // Icon on the left — matches collection style. Anchored to the top of the
        // card (slotY - slotH/2 + 22) so it stays aligned with the name line even
        // though the description now stretches the card vertically.
        const iconCY = slotY - slotH / 2 + 22;
        if (this.textures.exists(sigil.special.iconKey)) {
          const icon = this.add.image(cx - slotW / 2 + 20, iconCY, sigil.special.iconKey)
            .setDisplaySize(26, 26);
          this.bodyRoot.add(icon);
        }
        // Sigil name — sits beside the icon.
        const textX = cx - slotW / 2 + 38;
        this.bodyRoot.add(this.add.text(textX, iconCY - 6, sigil.name, {
          fontSize: '11px', fontFamily: 'Georgia, serif', color: '#f0c040', fontStyle: 'bold',
          wordWrap: { width: slotW - 44 },
        }).setOrigin(0, 0.5));
        // Affix summary — directly below the name.
        const affixStr = sigil.affixes.map(formatAffix).join(', ') || sigil.special.name;
        this.bodyRoot.add(this.add.text(textX, iconCY + 8, affixStr, {
          fontSize: '9px', fontFamily: 'Georgia, serif', color: '#a07840',
          wordWrap: { width: slotW - 44 },
        }).setOrigin(0, 0.5));
        // Special description — muted italic below the affix line so the player
        // can see WHAT the sigil does in-run, not just its stat rolls. Wraps to
        // the remaining card width; card height was sized to accommodate 2 lines.
        this.bodyRoot.add(this.add.text(cx - slotW / 2 + 8, iconCY + 22, sigil.special.description, {
          fontSize: '8px', fontFamily: 'Georgia, serif', color: '#7a5c2a', fontStyle: 'italic',
          wordWrap: { width: slotW - 16 }, align: 'left',
        }).setOrigin(0, 0));

        // Insure checkbox — tucked bottom-right of the slot card
        const boxX = cx + slotW / 2 - 74;
        const boxY = slotY + slotH / 2 - 10;
        const insured = pick?.insured ?? false;
        const box = this.add.rectangle(boxX, boxY, 11, 11, insured ? 0xc8973a : 0x0a0604, 1)
          .setStrokeStyle(1, 0x8a6420).setInteractive({ useHandCursor: true });
        this.bodyRoot.add(box);
        const label = this.add.text(boxX + 8, boxY, `Insure (${INSURANCE_PRICE} ◆)`, {
          fontSize: '9px', fontFamily: 'Georgia, serif', color: insured ? '#c8973a' : '#8a6420',
        }).setOrigin(0, 0.5).setInteractive({ useHandCursor: true });
        this.bodyRoot.add(label);
        const toggleInsure = () => {
          if (this.picks[i]) this.picks[i] = { ...this.picks[i]!, insured: !this.picks[i]!.insured };
          this.rebuildBody();
        };
        box.on('pointerdown', toggleInsure);
        label.on('pointerdown', toggleInsure);

        // Click card body (avoiding the insure checkbox area) to unequip.
        const hit = this.add.rectangle(cx - 40, slotY - 4, slotW - 90, slotH - 18, 0x000000, 0.001)
          .setInteractive({ useHandCursor: true });
        this.bodyRoot.add(hit);
        hit.on('pointerdown', () => {
          this.picks[i] = null;
          this.rebuildBody();
        });
      } else {
        this.bodyRoot.add(this.add.text(cx, slotY + 4, 'empty — pick below', {
          fontSize: '10px', fontFamily: 'Georgia, serif', color: '#5a3600', fontStyle: 'italic',
        }).setOrigin(0.5));
      }
    }

    // ── Collection list — row cards matching the inventory collection view ──
    const listHeaderY = slotY + 50;
    this.bodyRoot.add(this.add.text(PANEL_CX, listHeaderY, '— available sigils —', {
      fontSize: '10px', fontFamily: 'Georgia, serif', color: '#7a5c2a',
    }).setOrigin(0.5));

    // Row height bumped from 34 → 50 so the special.description line fits below
    // the affix summary. Wider sigils in the available list benefit most since
    // the player is picking blind — the description is the deciding factor.
    const rowH = 50;
    const rowW = PANEL_W - 60;
    const rowX = PANEL_CX;
    const listStartY = listHeaderY + 16;
    // Rows that fit between listStartY and (footerY - 20)
    const listEndY = PANEL_BOTTOM - 56;
    const maxRows = Math.floor((listEndY - listStartY) / rowH);
    const pickedIds = new Set(this.picks.filter((p): p is { sigilId: string; insured: boolean } => p !== null).map((p) => p.sigilId));

    const rowsToShow = Math.min(maxRows, this.collection.length);
    for (let i = 0; i < rowsToShow; i++) {
      const sigil = this.collection[i];
      const rowY = listStartY + i * rowH + rowH / 2;
      const alreadyPicked = pickedIds.has(sigil.id);

      const bg = this.add.rectangle(rowX, rowY, rowW, rowH - 4, alreadyPicked ? 0x050403 : 0x131110, 0.92)
        .setStrokeStyle(1, alreadyPicked ? 0x2a1a08 : 0x5a3a18)
        .setAlpha(alreadyPicked ? 0.55 : 1);
      this.bodyRoot.add(bg);
      if (!alreadyPicked) bg.setInteractive({ useHandCursor: true });

      // Icon on the left — identical sizing to InventoryScene sigil list.
      if (this.textures.exists(sigil.special.iconKey)) {
        const icon = this.add.image(rowX - rowW / 2 + 22, rowY, sigil.special.iconKey)
          .setDisplaySize(24, 24).setAlpha(alreadyPicked ? 0.5 : 1);
        this.bodyRoot.add(icon);
      }

      // Name + affix summary to the right of the icon. Row grew to 50px so we
      // shift content up and append a muted description line along the bottom.
      const textX = rowX - rowW / 2 + 42;
      this.bodyRoot.add(this.add.text(textX, rowY - 14, sigil.name, {
        fontSize: '11px', fontFamily: 'Georgia, serif',
        color: alreadyPicked ? '#3a2808' : '#c8973a', fontStyle: 'bold',
      }).setOrigin(0, 0.5).setAlpha(alreadyPicked ? 0.6 : 1));
      const affixStr = sigil.affixes.map(formatAffix).join(', ') || sigil.special.name;
      this.bodyRoot.add(this.add.text(textX, rowY - 1, affixStr, {
        fontSize: '9px', fontFamily: 'Georgia, serif',
        color: alreadyPicked ? '#3a2808' : '#6b4020',
        wordWrap: { width: rowW - 100 },
      }).setOrigin(0, 0.5).setAlpha(alreadyPicked ? 0.6 : 1));
      // Special description — the WHAT the sigil actually does. Italic + muted
      // so it's clearly secondary to the name + affixes but still readable.
      this.bodyRoot.add(this.add.text(textX, rowY + 12, sigil.special.description, {
        fontSize: '8px', fontFamily: 'Georgia, serif',
        color: alreadyPicked ? '#2a1a08' : '#7a5c2a', fontStyle: 'italic',
        wordWrap: { width: rowW - 100 },
      }).setOrigin(0, 0.5).setAlpha(alreadyPicked ? 0.55 : 1));

      if (alreadyPicked) {
        this.bodyRoot.add(this.add.text(rowX + rowW / 2 - 12, rowY, 'EQUIPPED', {
          fontSize: '8px', fontFamily: 'Georgia, serif', color: '#f0c040', fontStyle: 'bold',
        }).setOrigin(1, 0.5).setAlpha(0.75));
      } else {
        bg.on('pointerdown', () => {
          // Prefer an empty slot; if none, replace the LAST slot so clicking another sigil
          // works as a swap without manually unequipping first.
          const freeSlot = this.picks.findIndex((p) => p === null);
          const targetSlot = freeSlot !== -1 ? freeSlot : this.picks.length - 1;
          this.picks[targetSlot] = { sigilId: sigil.id, insured: false };
          this.rebuildBody();
        });
      }
    }

    if (this.collection.length > rowsToShow) {
      this.bodyRoot.add(this.add.text(PANEL_CX, listEndY - 2,
        `(showing ${rowsToShow} of ${this.collection.length} — manage the full collection from the main menu)`, {
          fontSize: '9px', fontFamily: 'Georgia, serif', color: '#5a3600', fontStyle: 'italic',
        }).setOrigin(0.5));
    }
  }

  private confirmAndStart(skipAll: boolean): void {
    const finalPicks = skipAll
      ? []
      : this.picks.filter((p): p is { sigilId: string; insured: boolean } => p !== null);
    const ok = this.gsm.startRunWithSigils(finalPicks);
    if (!ok) {
      // Not enough runes for the chosen insurance. Flash the total in red;
      // don't start the run.
      this.totalTxt.setColor('#ff4030');
      this.tweens.add({ targets: this.totalTxt, alpha: { from: 0.3, to: 1 }, duration: 140, yoyo: true, repeat: 2 });
      return;
    }
    this.scene.start('BookBanScene');
  }
}
